import React from 'react';
import { Helmet } from 'react-helmet-async';
import { clientContent } from '../../client/content';
import Section from '../../components/Section';
import CtaBlock from '../../components/CtaBlock';
import HowItWorks from '../../components/HowItWorks';
import styles from './WhatWeDo.module.scss';

const WhatWeDo: React.FC = () => {
  const { title, description, canonical } = clientContent.seo.whatWeDo;
  const { title: pageTitle, subtitle, boundaries } = clientContent.whatWeDo;

  return (
    <div className={styles.whatWeDo} data-testid="what-we-do-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Page Header */}
      <section className="pageHeader">
        <div className="container">
          <span className="eyebrow">Our Services & Scope</span>
          <h1>{pageTitle}</h1>
          <p>{subtitle}</p>
        </div>
      </section>

      {/* Detailed Website Section */}
      <Section background="default">
        <div className={styles.servicesHeader}>
          <h2>The website</h2>
          <p>We build your complete, professional website from scratch—crafted with clean code, tailored content, and robust foundations so your business stands out online.</p>
        </div>
        <div className={styles.boxesGrid}>
          <div className={styles.serviceBox}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
            </div>
            <h3>Professional design</h3>
            <p>Clean, bespoke-looking websites with strong typography, excellent visual hierarchy, and professional layout tailored to your business.</p>
          </div>
          <div className={styles.serviceBox}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
            </div>
            <h3>Mobile responsive</h3>
            <p>Fully optimised layout that looks and works seamlessly across mobile phones, tablets, and desktop screens.</p>
          </div>
          <div className={styles.serviceBox}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
            </div>
            <h3>Custom content &amp; pages</h3>
            <p>Structured around your specific services, business history, and messaging without generic filler content.</p>
          </div>
          <div className={styles.serviceBox}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
            </div>
            <h3>Contact &amp; Enquiries</h3>
            <p>Reliable enquiry channels making it straightforward for potential customers to get in touch with you directly.</p>
          </div>
          <div className={styles.serviceBox}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
            </div>
            <h3>Fast &amp; Lightweight</h3>
            <p>Built to load quickly without unnecessary plugins, databases, or bulky software.</p>
          </div>
          <div className={styles.serviceBox}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
            </div>
            <h3>Optimised images &amp; assets</h3>
            <p>Properly formatted and compressed imagery maintaining visual quality while keeping performance high.</p>
          </div>
          <div className={styles.serviceBox}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
            </div>
            <h3>Built around your business</h3>
            <p>Every website is structured around your services, customers and goals rather than forcing your business into a generic template.</p>
          </div>
          <div className={styles.serviceBox}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </div>
            <h3>SEO foundations</h3>
            <p>Built-in technical search engine foundations including semantic HTML, clean code, and correct metadata.</p>
          </div>
        </div>
      </Section>

      {/* Service Technical Section */}
      <Section background="surface">
        <div className={styles.technicalSection}>
          <div className={styles.technicalHeader}>
            <h2>The technical bits</h2>
            <p>We take care of everything technical, including hosting, SSL/HTTPS, deployment, DNS, redirects, technical maintenance, content changes, and ongoing support.</p>
          </div>

          <div className={styles.techBoxesGrid}>
            <div className={styles.techBox}>
              <div className={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path></svg>
              </div>
              <h3>Hosting &amp; Deployment</h3>
              <p>Reliable, high-performance web hosting and seamless deployment handled completely for you.</p>
            </div>
            <div className={styles.techBox}>
              <div className={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
              </div>
              <h3>HTTP &amp; SSL</h3>
              <p>Secure SSL certificate setup and renewal for visitor trust and data encryption.</p>
            </div>
            <div className={styles.techBox}>
              <div className={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
              </div>
              <h3>DNS &amp; Domain Setup</h3>
              <p>Professional domain record configuration, routing, and proper domain setup.</p>
            </div>
            <div className={styles.techBox}>
              <div className={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
              </div>
              <h3>Redirects &amp; Routing</h3>
              <p>Clean URL routing, permanent redirects, and domain link maintenance.</p>
            </div>
            <div className={styles.techBox}>
              <div className={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              </div>
              <h3>Content Changes</h3>
              <p>Text and content updates handled after launch without complex dashboards.</p>
            </div>
            <div className={styles.techBox}>
              <div className={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
              </div>
              <h3>Ongoing Support</h3>
              <p>Continuous technical maintenance and a direct line to your developer whenever you need help.</p>
            </div>
          </div>

          <div className={styles.propositionBanner}>
            <p>This reinforces your core proposition:</p>
            <strong>You run your business. We take care of your website.</strong>
          </div>
        </div>
      </Section>

       {/* Service Boundaries Section */}
      <Section background="default">
        <div className={styles.boundariesSection}>
          <h2>{boundaries.heading}</h2>
          <p className={styles.boundariesDesc}>{boundaries.description}</p>

          <div className={styles.boundariesGrid}>
            {/* Supported */}
            <div className={`${styles.boundaryCard} ${styles.supported}`}>
              <h3>
                <span className={styles.icon}>✓</span> What We Specialise In
              </h3>
              <ul>
                {boundaries.supported.map((item, index) => (
                  <li key={index}>
                    <span className={styles.icon}>•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Not Supported */}
            <div className={`${styles.boundaryCard} ${styles.notSupported}`}>
              <h3>
                <span className={styles.icon}>✕</span> What We Do Not Do
              </h3>
              <ul>
                {boundaries.notSupported.map((item, index) => (
                  <li key={index}>
                    <span className={styles.icon}>•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className={styles.boundaryNote}>We no longer build large, complex software applications as our customers do not need them.</p>
        </div>
      </Section>

      {/* How it works Section */}
      <HowItWorks />

      {/* Final Enquiry CTA */}
      <Section background="default">
        <CtaBlock
          title="Have a project in mind?"
          subtitle="Get in touch to discuss your business and find out how we can help you establish a professional web presence."
          buttonText="Get in Touch"
          buttonPath="/contact"
          secondaryButtonText="Explore Concepts"
          secondaryButtonPath="/concepts"
        />
      </Section>
    </div>
  );
};

export default WhatWeDo;
